import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Cpu,
  Zap,
  Sparkles,
  Heart,
  ShieldCheck,
  Settings,
  Award,
  CheckCircle2,
} from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import FloatingPetals from "../components/FloatingPetals";
import Reveal from "../components/Reveal";
import BookLink from "../components/BookLink";
import useSeo from "../hooks/useSeo";
import { images, business } from "../data/content";

export default function Technology() {
  useSeo({
    title: `Technology | Apilus xCell Pro | Soft Petal Electrolysis`,
    description: `Soft Petal Electrolysis uses the Apilus xCell Pro, one of the most advanced electrolysis systems available today, for faster, more comfortable, 100% permanent hair removal in ${business.city}, ${business.state}.`,
    path: "/technology",
    ogImage: images.apilus,
  });

  const benefits = [
    {
      icon: Cpu,
      title: "Advanced technology",
      copy: "Precise, effective treatments powered by industry-leading electronics.",
    },
    {
      icon: Zap,
      title: "Faster treatment times",
      copy: "Substantially quicker than older electrolysis systems.",
    },
    {
      icon: Heart,
      title: "Increased comfort",
      copy: "Gentler sensations throughout your appointment.",
    },
    {
      icon: Settings,
      title: "Personalized settings",
      copy: "Programs tailored to your unique skin and hair.",
    },
    {
      icon: Award,
      title: "Industry-leading equipment",
      copy: "The confidence of world-class technology in every session.",
    },
    {
      icon: ShieldCheck,
      title: "100% permanent",
      copy: "The gold standard for true, lasting hair removal.",
    },
  ];

  const specs = [
    { label: "27.12 MHz radio frequency", detail: "Exclusive Apilus frequency for precise, efficient results" },
    { label: "Intuitive touchscreen", detail: "Seamless control and personalization during your session" },
    { label: "Personalized programs", detail: "Tailored settings for every skin tone and hair type" },
    { label: "Latest innovation", detail: "One of the most sophisticated electrolysis devices available" },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="watercolor-blush py-16 lg:py-20 relative overflow-hidden">
        <FloatingPetals count={6} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Our Technology"
            title="Why we choose the"
            scriptWord="Apilus xCell Pro"
            subtitle="At Soft Petal Electrolysis, your comfort and results are our top priority. That's why every Soft Petal Pal is treated using one of the most advanced electrolysis systems available today."
          />
        </div>
      </section>

      {/* MACHINE + INTRO */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden bg-warm-white shadow-soft border border-pale-rose/40 p-6 sm:p-10 flex items-center justify-center">
                <img
                  src={images.apilus}
                  alt="Apilus xCell Pro electrolysis device with touchscreen used at Soft Petal Electrolysis"
                  loading="lazy"
                  className="w-full h-auto max-w-md rounded-xl block"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 w-24 h-24 rounded-full bg-blush flex items-center justify-center shadow-soft float-gentle">
                <div className="text-center">
                  <div className="font-script text-petal text-xl leading-none">27.12</div>
                  <div className="font-body text-[10px] tracking-widest uppercase text-petal">MHz</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={140} className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-petal font-body text-xs tracking-[0.3em] uppercase">
              <Cpu size={14} /> Cutting-edge care
            </div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl leading-tight">
              Precision, comfort,{" "}
              <span className="font-script text-petal text-[1.15em]">and results</span>
            </h2>
            <p className="mt-6 font-body text-charcoal leading-relaxed">
              The Apilus xCell Pro combines cutting-edge technology with exceptional precision to
              make treatments faster, more comfortable, and highly effective. Featuring its
              exclusive <span className="text-petal font-medium">27.12 MHz radio frequency</span>,
              personalized treatment programs, and intuitive touchscreen technology, it represents
              the latest innovation in <span className="text-petal font-medium">100% permanent
              hair removal</span>.
            </p>
            <ul className="mt-8 space-y-3">
              {specs.map((s) => (
                <li key={s.label} className="flex items-start gap-3">
                  <Sparkles size={16} className="text-petal mt-1 shrink-0" />
                  <div>
                    <div className="font-body text-charcoal font-medium">{s.label}</div>
                    <div className="font-body text-sm text-charcoal-soft">{s.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS GRID */}
      <section className="py-14 lg:py-20 bg-blush/40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="What this means for you"
            title="Every session,"
            scriptWord="elevated."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal
                  key={b.title}
                  delay={i * 100}
                  className="bg-warm-white rounded-3xl p-7 border border-pale-rose/50 shadow-soft lift-on-hover"
                  data-testid={`tech-benefit-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-blush flex items-center justify-center">
                    <Icon size={20} className="text-petal" />
                  </div>
                  <h3 className="mt-5 font-serif-display text-xl leading-tight">{b.title}</h3>
                  <p className="mt-2 font-body text-sm text-charcoal-soft leading-relaxed">
                    {b.copy}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLOSING PROMISE */}
      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionTitle title="You deserve the" scriptWord="very best." />
          <p className="mt-8 font-body text-lg text-charcoal leading-relaxed">
            As a Soft Petal Pal, you can feel confident knowing you&rsquo;re receiving care with
            one of the most sophisticated electrolysis devices available, because you deserve the
            very best on your permanent hair removal journey.
          </p>
          <p className="mt-6 font-serif-display italic text-2xl text-petal">
            Experience the difference that advanced technology can make.
          </p>
          <p className="mt-3 font-script text-petal text-3xl">
            Your smooth skin starts here.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <BookLink  className="btn-petal" data-testid="tech-cta-book">
              Book Your Appointment
              <ArrowRight size={14} />
            </BookLink>
            <Link to="/services" className="btn-ghost-petal" data-testid="tech-cta-services">
              View Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
